import { hasAcceptableClasses } from '../dynamic/Dynamic'

export namespace ParagraphSplitter {
  interface ISentenceSplit {
    sentence: string
    charCount: number
  }

  export const defaultMinCharCount = 60

  export const splitAndReplaceElements = (
    elements: Array<HTMLElement>
  ): Array<HTMLElement> => {
    const newElements: Array<HTMLElement> = []
    for (const el of elements) {
      const { parentNode } = el

      /** Filter out known elements sensitive to modification */
      if (!hasAcceptableClasses(el, /wprm/)) {
        newElements.push(el)
        continue
      }

      /** Don't mess with images */
      if (el.innerHTML.match(/img/)) {
        newElements.push(el)
        continue
      }

      let skipParentSplit = false
      if (el.children.length > 0) {
        for (const child of Array.from(el.children)) {
          const style = getComputedStyle(child)
          // Break down further if its a block element
          // Skip inline'rs to prevent counting height more than once.
          if (style.display === 'block') {
            skipParentSplit = true
            newElements.push(...splitAndReplaceElements([child as HTMLElement]))
          }
        }
      }

      if (skipParentSplit) {
        continue
      }

      const newEls = splitParagraph(el)
      newEls.forEach((newEl) => parentNode?.insertBefore(newEl, el))
      parentNode?.removeChild(el)
      newElements.push(...newEls)
    }

    return newElements
  }

  export const splitParagraph = <T extends HTMLElement>(
    element: T
  ): Array<T> => {
    if (element.nodeName !== 'P') {
      return [element.cloneNode(true) as T]
    }

    let newInnerHTML = ''
    let currentParagraph = ''
    let charTotal = 0

    const text = element.innerHTML
    if (!text) {
      return [element]
    }
    const sentences = getSplitGenerator(text)
    let split: IteratorResult<ISentenceSplit> = sentences.next()

    while (!split.done) {
      const { charCount, sentence } = split.value
      if (charCount === 0) {
        currentParagraph += sentence
        split = sentences.next()
        charTotal += charCount

        continue
      }

      charTotal += charCount
      currentParagraph += `${sentence}`

      if (charTotal > defaultMinCharCount) {
        newInnerHTML += `<p>${currentParagraph}</p>`
        charTotal = 0
        currentParagraph = ''
      }

      split = sentences.next()
    }

    // last iteration value
    newInnerHTML += `${currentParagraph}`
    const newEl = document.createElement(element.nodeName)
    newEl.innerHTML = newInnerHTML

    return Array.from(newEl.children) as Array<T>
  }

  // Where I expect to expand on the splitting logic.
  // IE. Don't split on `Mr. Dr. Mrs., etc`
  export const getSplitGenerator = function*(
    paragraph: string
  ): Generator<ISentenceSplit> {
    const sentences = puncuationToSplitOn.reduce(
      (acc, punc) => splitOnPuncuation(acc, punc),
      [paragraph]
    )

    for (const sentence of sentences) {
      yield { sentence, charCount: sentence.length }
    }
  }
}

export const puncuationToSplitOn = [/\.\s+/g, /!\s+/g, /\?\s+/g]
export const splitOnPuncuation = (
  strings: Array<string>,
  puncuation: RegExp
): Array<string> => {
  /**
   * This replaces our punctuation with a placeholder macro.
   * The placeholder allows us to target the split position
   * without removing the targeted puncuation.
   *
   * Meaning, if we split on '.', we'd end up losing the period.
   * So instead, we add a ** behind it, then split on that **.
   * */
  const modifiedStrArrays = strings.map((str) =>
    str.replace(puncuation, '$&**').split('**')
  )
  return modifiedStrArrays.reduce((acc, val) => [...acc, ...val], [])
}
